import React, { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional heading rendered in a bordered header row. */
  title?: string;
  children: ReactNode;
  /** Optional actions row rendered in a bordered footer (respects the safe area). */
  footer?: ReactNode;
  /** Max panel width at sm+ breakpoints. Default 'lg'. */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  /** Element to focus when the modal opens. */
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
}

// Literal class names (not interpolated) so Tailwind's content scanner keeps them.
const maxWidthClasses: Record<NonNullable<ModalProps['maxWidth']>, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
};

/**
 * Modal
 *
 * A reusable, mobile-first dialog built on @headlessui/react. Renders as a
 * bottom-anchored sheet on mobile (`items-end`) and a centered card on desktop
 * (`sm:block` + `sm:align-middle`), mirroring the proven ConfirmDialog shell.
 * The body scrolls internally on small screens and the footer respects the
 * iOS home-indicator safe area.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'lg',
  initialFocus,
}) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-50 inset-0 overflow-y-auto"
        initialFocus={initialFocus}
        onClose={onClose}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* Trick the browser into centering the modal on desktop. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className={`inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:align-middle sm:w-full ${maxWidthClasses[maxWidth]}`}
            >
              {title && (
                <div className="px-4 py-4 border-b border-gray-200 sm:px-6">
                  <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                    {title}
                  </Dialog.Title>
                </div>
              )}
              <div className="px-4 py-4 sm:px-6 max-h-[70vh] overflow-y-auto">
                {children}
              </div>
              {footer && (
                <div className="px-4 py-4 pb-safe-b border-t border-gray-200 sm:px-6">
                  {footer}
                </div>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
